import { Routes, UrlTree, UrlSerializer, DefaultUrlSerializer, ActivatedRouteSnapshot } from '@angular/router';
import { EnhancedAuthGuard } from '../guards/enhanced-auth.guard';

/**
 * Helper function to create redirects that preserve query parameters and fragments.
 *
 * Angular Router's string-based redirectTo loses query params and fragments during navigation.
 * Function-based redirectTo allows us to manually preserve them.
 *
 * @param targetPath - The destination path (e.g., '/dashboard/activity')
 * @returns A redirectTo function that preserves query params and fragments
 */
export function preserveParamsRedirect(targetPath: string): (route: ActivatedRouteSnapshot) => UrlTree | string {
  const serializer: UrlSerializer = new DefaultUrlSerializer();

  return (route: ActivatedRouteSnapshot): UrlTree | string => {
    // Build the target URL tree
    const urlTree = serializer.parse(targetPath);

    // Preserve query parameters from source route
    urlTree.queryParams = { ...route.queryParams };

    // Preserve fragment from source route
    if (route.fragment) {
      urlTree.fragment = route.fragment;
    }

    return urlTree;
  };
}

/**
 * Angular Router Redirects Configuration
 *
 * Migrates UI-Router rules engine redirects to Angular Router configuration.
 * This replaces the redirect rules from UIRouter.urlService.rules.when() calls
 * in the AppRoutesConfig function.
 *
 * Original UI-Router Rules (src/routes/app.routes.ts:592-606):
 * - uiRouter.urlService.rules.when('/dashboard', '/dashboard/activity');
 * - uiRouter.urlService.rules.when('/profile', '/profile/overview');
 * - uiRouter.urlService.rules.when('', '/login');
 * - uiRouter.urlService.rules.when('/carrierRequest', '/dashboard/carrierRequest');
 * - uiRouter.urlService.rules.when('/licenses', '/dashboard/licenses');
 * - uiRouter.urlService.rules.when('/framedTransaction', '/dashboard/framedTransaction');
 * - uiRouter.urlService.rules.when('/signup', '/signup/agency/agencyInfo');   // REMOVED: agency signup wizard deleted from sircon-ui
 * - uiRouter.urlService.rules.when('/signup/agency', '/signup/agency/agencyInfo'); // REMOVED: see above
 * - uiRouter.urlService.rules.when('/inviteSignup', '/inviteSignup/landing');
 */

export const ANGULAR_REDIRECT_ROUTES: Routes = [

  // ===============================
  // ROOT AND DEFAULT REDIRECTS
  // ===============================

  /**
   * Root path redirect: '' → '/login'
   * Equivalent to: uiRouter.urlService.rules.when('', '/login');
   */
  {
    path: '',
    redirectTo: preserveParamsRedirect('/login'),
    pathMatch: 'full'
  },

  /**
   * Dashboard default redirect: '/dashboard' → '/dashboard/activity'
   * Equivalent to: uiRouter.urlService.rules.when('/dashboard', '/dashboard/activity');
   */
  {
    path: 'dashboard',
    redirectTo: preserveParamsRedirect('/dashboard/activity'),
    pathMatch: 'full'
  },

  /**
   * Profile default redirect: '/profile' → '/profile/overview'
   * Equivalent to: uiRouter.urlService.rules.when('/profile', '/profile/overview');
   */
  {
    path: 'profile',
    redirectTo: preserveParamsRedirect('/profile/overview'),
    pathMatch: 'full'
  },

  // ===============================
  // LEGACY ROUTE REDIRECTS
  // ===============================
  // These redirect old URLs that were referenced in notifications

  /**
   * Legacy carrier request redirect
   * Equivalent to: uiRouter.urlService.rules.when('/carrierRequest', '/dashboard/carrierRequest');
   */
  {
    path: 'carrierRequest',
    redirectTo: preserveParamsRedirect('/dashboard/carrierRequest'),
    pathMatch: 'full'
  },

  /**
   * Legacy licenses redirect
   * Equivalent to: uiRouter.urlService.rules.when('/licenses', '/dashboard/licenses');
   */
  {
    path: 'licenses',
    redirectTo: preserveParamsRedirect('/dashboard/licenses'),
    pathMatch: 'full'
  },

  /**
   * Legacy framed transaction redirect
   * Equivalent to: uiRouter.urlService.rules.when('/framedTransaction', '/dashboard/framedTransaction');
   */
  {
    path: 'framedTransaction',
    redirectTo: preserveParamsRedirect('/dashboard/framedTransaction'),
    pathMatch: 'full'
  },

  // ===============================
  // SIGNUP FLOW REDIRECTS
  // ===============================

  // NOTE: The original UI-Router rules redirected /signup and /signup/agency to
  // /signup/agency/agencyInfo. In sircon-tools-ui this state was renamed to
  // signup.firm / signup.firm.firmInfo, but in sircon-ui the entire agency/firm signup
  // wizard was removed and replaced with the signup/activation + signup/acceptInvitation
  // flow. No agencyInfo or firmInfo component or route exists in this app.
  // These redirects have been removed; bare /signup and /signup/agency fall through to
  // the wildcard (__) catch-all and land on /error, which is the correct behaviour for
  // a removed feature. The Java smoke tests referencing ui-sref="signup.agency.agencyInfo"
  // are also stale and should be removed from the smoke test suite.

  /**
   * Invite signup redirect
   * Equivalent to: uiRouter.urlService.rules.when('/inviteSignup', '/inviteSignup/landing');
   */
  {
    path: 'inviteSignup',
    redirectTo: preserveParamsRedirect('/inviteSignup/landing'),
    pathMatch: 'full'
  },

  // ===============================
  // ADVANCED REDIRECT PATTERNS
  // ===============================

  /**
   * Parameterized redirect example
   * Redirects with query parameters preserved
   */
  {
    path: 'old-dashboard/:id',
    redirectTo: '/dashboard/:id',
    pathMatch: 'full'
  },

  /**
   * Conditional redirect using a guard
   * For more complex redirect logic that depends on user state
   */
  {
    path: 'smart-redirect',
    canActivate: [EnhancedAuthGuard],
    children: [
      {
        path: '',
        redirectTo: preserveParamsRedirect('/dashboard/activity'),
        pathMatch: 'full'
      }
    ]
  },

  // ===============================
  // WILDCARD / FALLBACK
  // ===============================

  /**
   * Catch-all redirect (equivalent to uiRouter.urlService.rules.otherwise())
   * This should be the LAST route in your main routing configuration
   * Commented out here since it should go in the main app.routes.ts
   */
  // {
  //   path: '**',
  //   redirectTo: '/error',
  //   pathMatch: 'full'
  // }
];

/**
 * Helper function for dynamic redirects based on user context
 * Use this when redirect logic depends on runtime conditions
 */
export function createConditionalRedirect(
  condition: () => boolean,
  truePath: string,
  falsePath: string
): Routes {
  return [
    {
      path: '',
      canActivate: [EnhancedAuthGuard],
      // Resolver removed - userContext now resolved at root level in app.routes.ts
      // Children inherit resolved data via Angular's route data inheritance
      children: [
        {
          path: '',
          redirectTo: (route: ActivatedRouteSnapshot) => {
            const targetPath = condition() ? truePath : falsePath;
            return preserveParamsRedirect(targetPath)(route);
          },
          pathMatch: 'full'
        }
      ]
    }
  ];
}

/**
 * Migration Notes:
 *
 * 1. **pathMatch: 'full'**: Essential for exact path matching, equivalent to UI-Router's exact matching
 *
 * 2. **Order Matters**: More specific routes should come before general ones
 *
 * 3. **Query Parameters**: String-based redirectTo loses query params; use preserveParamsRedirect() helper
 *
 * 4. **Guards with Redirects**: Can combine redirects with guards for conditional logic
 *
 * 5. **Dynamic Redirects**: Use guards or resolvers for complex redirect logic that depends on runtime state
 *
 * 6. **Wildcard Route**: The catch-all route (path: '**') should be the last route in your configuration
 */
